import { isDecimalAmount } from '@platform/design-system';
import type { Operation, RequestBody } from '@platform/openapi';
import { decimalOf } from '../catalog/product-form.ts';

export type AdjustStockBody = RequestBody<Operation<'/admin/stock-movements/batch', 'post'>>;
export type AdjustmentLine = AdjustStockBody['lines'][number];

export const ADJUSTMENT_REASONS = [
  'sale',
  'return',
  'defective',
  'loss',
] as const satisfies readonly AdjustmentLine['reason'][];

export type AdjustmentReason = (typeof ADJUSTMENT_REASONS)[number];

export const RECEIPT_REASON = 'receipt' satisfies AdjustmentLine['reason'];

export type StockOperation = AdjustmentReason | typeof RECEIPT_REASON;

const PRICED_REASONS: readonly StockOperation[] = ['sale'];

export const takesUnitPrice = (reason: StockOperation): boolean => PRICED_REASONS.includes(reason);

export const MAX_ADJUSTMENT_QUANTITY = 100_000;
export const MAX_ADJUSTMENT_LINES = 200;

export type AdjustmentPlan = {
  readonly reason: StockOperation;
  readonly locationId: string;
  readonly unitPrice: string;
  readonly note: string;
};

export type AdjustmentEntry = {
  readonly variantId: string;
  readonly onHand: number;
  readonly text: string;
};

export type EntryOutcome =
  | { readonly kind: 'empty' }
  | { readonly kind: 'invalid' }
  | { readonly kind: 'negative'; readonly after: number }
  | { readonly kind: 'ready'; readonly after: number; readonly change: number; readonly value: number };

const SIGN_OF_REASON: Readonly<Record<StockOperation, number>> = {
  sale: -1,
  return: 1,
  defective: -1,
  loss: -1,
  receipt: 1,
};

export const hasValidUnitPrice = (plan: AdjustmentPlan): boolean =>
  !takesUnitPrice(plan.reason) || isDecimalAmount(decimalOf(plan.unitPrice));

const WHOLE_NUMBER = /^[+-]?\d+$/u;

const acceptsValue = (value: number): boolean => value > 0 && value <= MAX_ADJUSTMENT_QUANTITY;

export const evaluateEntry = (plan: AdjustmentPlan, entry: AdjustmentEntry): EntryOutcome => {
  const text = entry.text.trim();
  if (text === '') return { kind: 'empty' };
  if (!WHOLE_NUMBER.test(text)) return { kind: 'invalid' };
  const value = Number(text);
  if (!acceptsValue(value)) return { kind: 'invalid' };
  const change = SIGN_OF_REASON[plan.reason] * value;
  const after = entry.onHand + change;
  return after < 0 ? { kind: 'negative', after } : { kind: 'ready', after, change, value };
};

const lineOf = (plan: AdjustmentPlan, variantId: string, value: number): AdjustmentLine => {
  const note = plan.note.trim();
  return {
    variantId,
    locationId: plan.locationId,
    reason: plan.reason,
    quantity: value,
    counted: null,
    unitPrice: takesUnitPrice(plan.reason) ? decimalOf(plan.unitPrice) : null,
    note: note === '' ? null : note,
  };
};

export type AdjustmentReview = {
  readonly lines: readonly AdjustmentLine[];
  readonly blocked: boolean;
  readonly totalChange: number;
};

export const reviewAdjustment = (
  plan: AdjustmentPlan,
  entries: readonly AdjustmentEntry[],
): AdjustmentReview => {
  const outcomes = entries.map((entry) => ({ entry, outcome: evaluateEntry(plan, entry) }));
  const ready = outcomes.flatMap(({ entry, outcome }) =>
    outcome.kind === 'ready' && outcome.change !== 0 ? [{ entry, outcome }] : [],
  );
  return {
    lines: ready.map(({ entry, outcome }) => lineOf(plan, entry.variantId, outcome.value)),
    blocked:
      !hasValidUnitPrice(plan) ||
      outcomes.some(({ outcome }) => outcome.kind === 'invalid' || outcome.kind === 'negative'),
    totalChange: ready.reduce((total, { outcome }) => total + outcome.change, 0),
  };
};
